import { S3Client } from "@aws-sdk/client-s3"
import { Upload } from "@aws-sdk/lib-storage"
import { Box, Button } from "@material-ui/core"
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { useMutation, useQueryClient } from "react-query"
import { useHistory } from "react-router-dom"
import * as tus from "tus-js-client"
import { createCloudflareVideo, requestSignedUrl } from "~/actions/upload"
import FileUpload from "~/components/Common/FileUpload"
import AppContext from "~/contexts/App"
import LessonContext from "~/contexts/Lesson"
import useOpenErrorSnackbar from "~/hooks/useOpenErrorSnackbar"
import { requestCloudflareCredentials } from "../../../actions/upload"
import LinearProgressWithLabel from "../LinearProgressWithLabel"

const CloudflareVideoUpload = () => {
  const { openSnackBar, openDialog } = useContext(AppContext)
  const { updateLessonMutation, videoUrl, lesson } = useContext(LessonContext)
  const queryClient = useQueryClient()
  const history = useHistory()
  const requestS3SignedUrlMutation = useMutation(requestSignedUrl)

  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadFinished, setUploadFinished] = useState(false)
  const [showLoading, setShowLoading] = useState(false)
  const [cloudFlareMediaId, setCloudFlareMediaId] = useState(null)
  const [videoKey, setVideoKey] = useState(null)
  const openErrorSnackbar = useOpenErrorSnackbar()
  const [progressMessage, setProgressMessage] = useState("")

  const createCloudflareVideoMutation = useMutation(createCloudflareVideo)

  const blockRef = useRef()

  const resetState = () => {
    setUploadProgress(0)
    setShowLoading(false)
    setProgressMessage("")
  }

  // const uploadVideoToS3 = (acceptedFiles, key) => {
  //   requestS3SignedUrlMutation.mutateAsync().then(async (data) => {
  //     AWS.config.update({
  //       accessKeyId: data.key,
  //       secretAccessKey: data.secret,
  //       sessionToken: data.session,
  //     })
  //     const s3 = new AWS.S3({
  //       params: { Bucket: import.meta.env.VITE_UPLOAD_BUCKET },
  //       region: "us-east-1",
  //     })
  //     const params = {
  //       Bucket: import.meta.env.VITE_UPLOAD_BUCKET,
  //       Key: key,
  //       ContentType: acceptedFiles[0].type,
  //       Body: acceptedFiles[0],
  //     }

  //     var upload = s3
  //       .putObject(params)
  //       .on("httpUploadProgress", (evt) => {

  //       })
  //       .promise()

  //     await upload.then((err, data) => {
  //       alert("File uploaded successfully.")
  //     })
  //   })
  // }
  const uploadVideoToS3 = (acceptedFiles, key) => {
    requestS3SignedUrlMutation.mutateAsync().then((data) => {
      const params = {
        Bucket: import.meta.env.VITE_UPLOAD_BUCKET,
        Key: key,
        ContentType: acceptedFiles[0].type,
        Body: acceptedFiles[0],
      }

      const s3Client = new S3Client({
        region: "us-east-1",
        credentials: {
          accessKeyId: data.key,
          secretAccessKey: data.secret,
          sessionToken: data.session,
        },
      })

      const upload = new Upload({
        client: s3Client,
        params,
      })

      // Listen to the httpUploadProgress event
      upload.on("httpUploadProgress", (progress) => {
        let uploaded = Math.round((progress.loaded / progress.total) * 100)
        console.info(`${uploaded}% to S3`)
      })

      var promise = new Promise(async (resolve, reject) => {
        try {
          const u = await upload.done()
          resolve(u)
        } catch (err) {
          reject(err)
        }
      })
      promise
        .then((res) => console.info("Upload to S3 complete"))
        .catch((err) => {
          console.error("Error uploading to s3", err)
          openErrorSnackbar(
            err,
            "There was an error uploading this video to S3",
          )
        })
    })
  }

  const onDrop = useCallback(async (acceptedFiles) => {
    const timestamp = +new Date()
    const key = `lessonVideos/${timestamp}-${acceptedFiles[0].name}`
    uploadVideoToS3(acceptedFiles, key)
    setUploadFinished(false)
    setShowLoading(true)
    setVideoKey(key)
    const file = acceptedFiles[0]

    const { uploadURL } = await requestCloudflareCredentials({
      uploadLength: file.size,
      name: key,
      metadata: {
        name: key,
        filename: acceptedFiles[0].name,
      },
    })

    setProgressMessage("Uploading Video")

    const options = {
      endpoint: uploadURL,
      chunkSize: 50 * 1024 * 1024,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      uploadSize: file.size,
      onError: function (error) {
        throw error
      },
      onProgress: function (bytesUploaded, bytesTotal) {
        var percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2)
        setUploadProgress(Number(percentage))
        if (Number(percentage) === 100) {
          setProgressMessage("Video uploaded to Cloudflare")
        }
      },
      onSuccess: function () {
        const urlParts = upload.url.split("/")

        const mediaIdWithParams = urlParts[urlParts.length - 1]
        const mediaId = mediaIdWithParams.split("?")[0]

        setCloudFlareMediaId(mediaId)
        setUploadFinished(true)
      },
    }

    var upload = new tus.Upload(file, options)
    upload.start()
  }, [])

  const onError = useCallback((rejectedFiles) => {
    openSnackBar({
      message:
        rejectedFiles[0].errors[0].code === "file-invalid-type"
          ? "You can only upload a video file here"
          : "There was an error uploading that file",
    })
  }, [])

  useEffect(() => {
    if (uploadFinished) {
      let data = {
        key: videoKey,
        cloudflare_video_id: cloudFlareMediaId,
      }
      if (lesson.video_id !== null) {
        data.video_id = lesson.video_id
      }
      createCloudflareVideoMutation
        .mutateAsync(data)
        .then((res) => {
          setProgressMessage("Video not ready to stream, come back soon")
          // update lesson video id
          updateLessonMutation.mutate(
            {
              video_id: res.data.id,
            },
            queryClient.setQueryData(["lesson", lesson.id], (oldData) => {
              oldData.duration = null
              oldData.video_id = res.data.id
              oldData.video = res.data
              oldData.captions = []
              return oldData
            }),
            lesson.video_id &&
              queryClient.invalidateQueries(["getVideoUrl", lesson?.video_id]),

            {
              onError: (updateLessonErr) => {
                console.error(updateLessonErr)
                openErrorSnackbar(
                  updateLessonErr,
                  "There was an error uploading this video",
                )
              },
            },
          )
          resetState()
        })
        .catch((err) => {
          openSnackBar({
            message: "Error uploading video",
          })
        })
    }
  }, [uploadFinished])

  useEffect(() => {
    if (showLoading) {
      blockRef.current = {
        unblock: history.block((tx) => {
          openDialog("unsavedDataDialog", {
            tx,
            text: "Your video is still uploading, leaving this page will cancel the upload.  Are you sure you want to continue?",
            discardButtonText: "Leave Page",
          }).then(() => {
            blockRef.current.unblock()
            history.push(tx.pathname)
          })
          return false
        }),
      }
    } else {
      blockRef.current?.unblock?.()
    }
  }, [showLoading])

  return (
    <>
      <FileUpload
        title="Drop Lesson Video here to Begin"
        DropZoneProps={{ accept: "video/*" }}
        onDrop={onDrop}
        onError={onError}
        showLoading={showLoading}
        progressMessage={progressMessage}
      >
        {videoUrl?.data && videoUrl?.data?.data?.readyToStream === true ? (
          <Box paddingY={3}>
            <Button color="secondary">Reupload / change video</Button>
          </Box>
        ) : null}
      </FileUpload>
      {uploadProgress > 0 && uploadProgress < 100 && (
        <LinearProgressWithLabel value={uploadProgress} color="secondary" />
      )}
    </>
  )
}

CloudflareVideoUpload.propTypes = {}

export default CloudflareVideoUpload
